import React, { useState } from 'react';

import { TableSelectionActions } from '../table-selection-actions/TableSelectionActions';

import { TestRowSharedType } from './helpers/types';
import { useProcessingStatus } from './hooks';
import { MODAL_ACTION_MESSAGES } from './labels';
import { LabelsManagementTableActionsProps } from './LabelsManagement.types';
import {
	LABEL_MANAGEMENT_MODAL_TYPE,
	LabelManagementModalType,
	LabelManagementSharedModal
} from './shared-modal';

export function LabelsManagementTableActions<T>(
	props: LabelsManagementTableActionsProps<T>
): React.ReactNode {
	const {
		selectedRows,
		handleClearSelection,
		promptText,
		itemName,
		addLabelButtonLabel,
		changeLabelValueButtonLabel,
		removeLabelsButtonLabel,
		onLabelActionSuccess,
		tenantConfiguredLabels,
		appType,
		deleteProcessingMessage
	} = props;

	const { processingMessage, startPolling } = useProcessingStatus(onLabelActionSuccess, appType);

	const effectiveProcessingMessage = processingMessage ?? deleteProcessingMessage ?? null;

	const [modalType, setModalType] = useState<LabelManagementModalType>(null);

	const onAddLabel = (): void => {
		setModalType(LABEL_MANAGEMENT_MODAL_TYPE.ADD);
	};

	const onUpdateLabel = (): void => {
		setModalType(LABEL_MANAGEMENT_MODAL_TYPE.UPDATE);
	};

	const onRemoveLabel = (): void => {
		setModalType(LABEL_MANAGEMENT_MODAL_TYPE.REMOVE);
	};

	const onCloseModal = (): void => {
		setModalType(null);
	};

	const onSaveModal = (): void => {
		let progressMessage: string;
		let successMessage: string;
		let errorMessage: string;

		switch (modalType) {
			case LABEL_MANAGEMENT_MODAL_TYPE.ADD:
				progressMessage = MODAL_ACTION_MESSAGES.LABEL_ADDITION_PROGRESS_MESSAGE;
				successMessage = `Labels added for ${selectedRows.length} ${itemName}${selectedRows.length === 1 ? '' : 's'}`;
				errorMessage = MODAL_ACTION_MESSAGES.LABEL_ADDITION_ERROR_MESSAGE;
				break;
			case LABEL_MANAGEMENT_MODAL_TYPE.REMOVE:
				progressMessage = MODAL_ACTION_MESSAGES.LABEL_REMOVAL_PROGRESS_MESSAGE;
				successMessage = `Labels removed for ${selectedRows.length} ${itemName}${selectedRows.length === 1 ? '' : 's'}`;
				errorMessage = MODAL_ACTION_MESSAGES.LABEL_REMOVAL_ERROR_MESSAGE;
				break;
			case LABEL_MANAGEMENT_MODAL_TYPE.UPDATE:
				progressMessage = MODAL_ACTION_MESSAGES.LABEL_UPDATE_PROGRESS_MESSAGE;
				successMessage = `Labels updated for ${selectedRows.length} ${itemName}${selectedRows.length === 1 ? '' : 's'}`;
				errorMessage = MODAL_ACTION_MESSAGES.LABEL_UPDATE_ERROR_MESSAGE;
				break;
			default:
				return;
		}

		onCloseModal();
		startPolling(progressMessage, successMessage, errorMessage);
	};

	return (
		<>
			<TableSelectionActions
				selectedCount={selectedRows.length}
				onClearSelection={handleClearSelection}
				processingMessage={effectiveProcessingMessage}
				promptText={promptText}
				itemName={itemName}
				addLabelButtonLabel={addLabelButtonLabel}
				changeLabelValueButtonLabel={changeLabelValueButtonLabel}
				removeLabelsButtonLabel={removeLabelsButtonLabel}
				onAddLabel={onAddLabel}
				onChangeLabelValue={onUpdateLabel}
				onRemoveLabels={onRemoveLabel}
			/>
			{modalType && (
				<LabelManagementSharedModal
					type={modalType}
					isOpen={true}
					onClose={onCloseModal}
					onSave={onSaveModal}
					testRows={selectedRows as TestRowSharedType[]}
					tenantConfiguredLabels={tenantConfiguredLabels}
					appType={appType}
				/>
			)}
		</>
	);
}
